export default class Form {

    constructor( form ) {

        if ( 'undefined' === typeof form || ! form ) {
            return;
        }

        this.form = form;
        this.inputFields = '';
        this.proceed = true;
        this.inputEnteredByClick = false;
        this.buttons = null;

        // Is or Isn't a legacy form (legacy = gforms lower than 2.5).
        this.legacyForm = !! this.form.parentNode.classList.contains( 'gform_legacy_markup_wrapper' );
        this.isDesktop = false;
        this.init();
        this.windowEvents();

        this.fieldsetsWithTooltips();
        this.disableMaterialWhenDescriptionAndTop();
    }

    /**
     * Firing multiple window events.
     */
    windowEvents() {
        window.addEventListener( 'DOMContentLoaded', () => this.checkIfIsDesktop() );
        window.addEventListener( 'resize', () => this.checkIfIsDesktop() );
        window.addEventListener( 'orientationchange', () => this.checkIfIsDesktop() );
    }

    /**
     * Check if the media-query matches a desktop width (atleast).
     */
    checkIfIsDesktop() {
        this.isDesktop = !! window.matchMedia( '(min-width: 991px)' ).matches;
    }

    /**
     * Initialize for each Form
     */
    init() {
        // All input-fields except hidden fields.
        this.inputFields = this.form.querySelectorAll( '[name]:not([type="hidden"])' );

        if ( ! this.inputFields || '' === this.inputFields ) {
            return;
        }

        this.buttons = this.form.querySelectorAll( 'input.gform_button' );

        // Loop through input fields.
        for ( let input of this.inputFields ) {
            this.setInputAttributes( input );

            // On events.
            input.addEventListener( "focusin", this.onFocus.bind( this ), true );
            input.addEventListener( "blur", this.onBlur.bind( this ), true );

            if ( 'INPUT' === input.tagName && ( 'radio' === input.type || 'checkbox' === input.type ) ) {
                input.addEventListener( "click", this.onBlur.bind( this ), true );
            }

            // on load.
            this.onValidate( input );

            // Set max on `select['multiple'][data-max-length']`.
            if ( 'SELECT' === input.nodeName && input.multiple && input.dataset.maxLength ) {
                this.selectMultipleMax( input, parseFloat( input.dataset.maxLength ) );
            }

            input.addEventListener( "keydown", ( e ) => this.isClickOrKeyPress( e ), true );
            input.addEventListener( "mousedown", ( e ) => this.isClickOrKeyPress( e ), true );
        }

        // Buttons will be applied with a custom-button (including Ripple).
        Array.from( this.buttons ).forEach( btn => btn.tabIndex = -1 );
    }

    /**
     * Due to the issue with Form-inputs where `:focus` and `:focus-visible` are working exactly the same
     *
     * @param {event} event The event.
     */
    isClickOrKeyPress( event ) {
        this.inputEnteredByClick = 'mousedown' === event?.type;

        // Remove all no-focus-visibles.
        Array.from( this.form.getElementsByClassName( 'no-focus-visible' ) ).forEach( el => el.classList.remove( 'no-focus-visible' ) );

        // Add only if we need it.
        if ( !! this.inputEnteredByClick ) {
            event?.target?.classList.add( 'no-focus-visible' )
        }
    }

    /**
     * Modify Input field, to , make user experience better.
     *
     * @source: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
     * @source: https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
     * @source: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/pattern
     * @param input
     */
    setInputAttributes( input ) {
        let parent = input.parentNode.parentNode;

        if ( parent.classList.contains( 'ginput_complex' ) || parent.classList.contains( 'clear-multi' ) ) {
            parent = input.parentNode;
        }

        input.classList.add( 'material-input' );
        parent.classList.add( 'material' );

        // Sizes, add class to parent for styling.
        if ( input.classList.contains( 'medium' ) ) {
            parent.classList.add( 'material--medium' );
        }
        if ( input.classList.contains( 'small' ) ) {
            parent.classList.add( 'material--small' );
        }

        if ( parent.classList.contains( 'material--number' ) ) {
            // Set correct input-type.
            input.setAttribute( 'type', 'number' );

            // Set correct keyboard.
            input.setAttribute( 'inputmode', 'nummeric' );
        }
        if ( parent.classList.contains( 'material--website' ) ) {
            // Set autocomplete.
            input.setAttribute( 'autocomplete', 'url' );

            // Set correct keyboard.
            input.setAttribute( 'inputmode', 'url' );
        }
        if ( parent.classList.contains( 'material--phone' ) ) {
            // Set correct keyboard.
            input.setAttribute( 'inputmode', 'tel' );
        }

        if ( parent.classList.contains( 'material--email' ) ) {
            // Set correct input-type.
            input.setAttribute( 'type', 'email' );
            // Set correct keyboard.
            input.setAttribute( 'inputmode', 'email' );
        }

        if ( parent.classList.contains( 'gfield_date_dropdown_year' )
            || parent.classList.contains( 'gfield_date_dropdown_month' )
            || parent.classList.contains( 'gfield_date_dropdown_day' )
        ) {
            this.addElement( 'label', input.getAttribute( 'aria-label' ), input, {
                for: input.getAttribute( 'id' ),
                class: 'material-label',
            } );
        }

        if ( 'SELECT' !== input.nodeName && ( parent.classList.contains( 'gfield_time_hour' ) || parent.classList.contains( 'gfield_time_minute' ) ) ) {
            // Digits only.
            input.setAttribute( 'pattern', "\\d+" );
        }

        if ( 'email' === input.type ) {
            // Set autocomplete.
            input.setAttribute( 'autocomplete', 'email' );

            // Set correct keyboard.
            input.setAttribute( 'inputmode', 'email' );
        }

        if ( 'TEXTAREA' === input.nodeName ) {

            // Set input row amount for legacy form.
            if ( input.classList.contains( 'small' ) && this.legacyForm ) {
                input.setAttribute( 'rows', 5 );
            }

            // Set input row amount for legacy form.
            if ( input.classList.contains( 'medium' ) && this.legacyForm ) {
                input.setAttribute( 'rows', 7 );
            }

            if ( ! this.legacyForm ) {
                input.setAttribute( 'rows', 7 );
            }

            // Classes contain typography styling.
            input.classList.remove( 'small' );
            input.classList.remove( 'medium' );
            input.classList.remove( 'large' );
        }

        if ( input.classList.contains( 'datepicker' ) ) {
            // Disable autocomplete.
            input.setAttribute( 'autocomplete', 'off' );

            // Set Input Pattern.
            if ( input.classList.contains( 'mdy' ) ) {
                input.setAttribute( 'pattern', this.getPattern( 'mdy' ) );
            }

            // Set Input Pattern.
            if ( input.classList.contains( 'dmy' ) ) {
                input.setAttribute( 'pattern', this.getPattern( 'dmy' ) );
            }

            // Set Input Pattern.
            if ( input.classList.contains( 'dmy_dash' ) ) {
                input.setAttribute( 'pattern', this.getPattern( 'dmy_dash' ) );
            }

            // Set Input Pattern.
            if ( input.classList.contains( 'dmy_dot' ) ) {
                input.setAttribute( 'pattern', this.getPattern( 'dmy_dot' ) );
            }

            // Set Input Pattern.
            if ( input.classList.contains( 'ymd_slash' ) ) {
                input.setAttribute( 'pattern', this.getPattern( 'ymd_slash' ) );
            }

            // Set Input Pattern.
            if ( input.classList.contains( 'ymd_dash' ) ) {
                input.setAttribute( 'pattern', this.getPattern( 'ymd_dash' ) );
            }

            // Set Input Pattern.
            if ( input.classList.contains( 'ymd_dot' ) ) {
                input.setAttribute( 'pattern', this.getPattern( 'ymd_dot' ) );
            }
        }

        // Set Input Pattern.
        if (
            (
                parent.classList.contains( 'name_first' )
                || parent.classList.contains( 'name_middle' )
                || parent.classList.contains( 'name_last' )
                || parent.classList.contains( 'name_suffix' )

                || parent.classList.contains( 'address_city' )
                || parent.classList.contains( 'address_state' )
            )
            && input.hasAttribute( 'pattern' )
        ) {
            // Alphabetic only.
            input.setAttribute( 'pattern', this.getPattern( 'alphabetical' ) );
        }

        if ( !! this.isDesktop ) {
            parent.classList.add( 'no-focus-visible' );
        }
    }

    /**
     * Returns pattern as string.
     * @param label
     * @return {string}
     */

    // @todo: Test patterns on `/`, `\`
    getPattern( label ) {
        switch ( label ) {
            case 'mdy':
                // Format 'mm/dd/yyyy', nummeric characters only, splitted by `/`.
                return "^(0[1-9]|1[012])[/](0[1-9]|[12][0-9]|3[01])[/](19|20)\\d\\d$";
            case 'dmy':
                // Format 'dd/mm/yyyy', nummeric characters only, splitted by `/`.
                return "^(0[1-9]|[12][0-9]|3[01])[/](0[1-9]|1[012])[/](19|20)\\d\\d$";
            case 'dmy_dash':
                // Format 'dd-mm-yyyy', nummeric characters only, splitted by `-`.
                return "^(0[1-9]|[12][0-9]|3[01])[-](0[1-9]|1[012])[-](19|20)\\d\\d$";
            case 'dmy_dot':
                // Format 'dd.mm.yyyy', nummeric characters only, splitted by `.`.
                return "^(0[1-9]|[12][0-9]|3[01])[.](0[1-9]|1[012])[.](19|20)\\d\\d$";
            case 'ymd_slash':
                // Format 'yyyy/mm/dd', nummeric characters only, splitted by `/`.
                return "^(19|20)\\d\\d[/](0[1-9]|1[012])[/](0[1-9]|[12][0-9]|3[01])$";
            case 'ymd_dash':
                // Format 'yyyy-mm-dd', nummeric characters only, splitted by `-`.
                return "^(19|20)\\d\\d[-](0[1-9]|1[012])[-](0[1-9]|[12][0-9]|3[01])$";
            case 'ymd_dot':
                // Format 'yyyy.mm.dd', nummeric characters only, splitted by `.`.
                return "^(19|20)\\d\\d[.](0[1-9]|1[012])[.](0[1-9]|[12][0-9]|3[01])$";
            case '***-***-***':
                // License Key, made based on alphabeticly & nummeric characters, splitted by `-`.
                return "^[[:alnum:]][[:alnum:]][[:alnum:]]-[[:alnum:]][[:alnum:]][[:alnum:]]-[[:alnum:]][[:alnum:]][[:alnum:]]/";
            case 'alphabetical':
                // Allow alphabetically characters only.
                return "^[a-zA-Z- ]*$";
        }
    }

    /**
     * Validate input fields.
     *
     * @param input
     */
    onValidate( input ) {

        let parent = input.parentNode.parentNode;

        if ( parent.classList.contains( 'ginput_complex' ) || parent.classList.contains( 'clear-multi' ) ) {
            parent = input.parentNode;
        }

        if ( parent.classList.contains( 'is-focus' ) && ! input.value ) {
            parent.classList.remove( 'is-focus' );
        }

        if ( input.tagName === 'SELECT' && input.value.trim() ) {
            parent.classList.add( 'is-focus' );
        }

        if ( parent.classList.contains( 'is-invalid' ) ) {
            parent.classList.remove( 'is-invalid' );
        }

        if ( ( 'checkbox' === input.type || 'radio' === input.type ) && input.checked ) {

            if ( parent.parentNode.parentNode.classList.contains( 'gfield_error' ) ) {
                parent.parentNode.parentNode.classList.remove( 'gfield_error' );

                // Hide validation message on default inputs.
                parent.parentNode.parentNode.querySelector( '.validation_message' ).style.display = 'none';
            }

            parent.parentNode.parentNode.classList.add( 'is-valid' );
            if ( ! parent.parentNode.parentNode.classList.contains( 'is-focus' ) ) {
                parent.parentNode.parentNode.classList.add( 'is-focus' );
            }
        }

        if ( ! input.value ) {
            parent.classList.remove( 'is-valid' );
            return;
        }

        if ( input.checkValidity() ) {
            // use Pattern HTML attribute
            parent.classList.add( 'is-valid' );

            if ( ! parent.classList.contains( 'is-focus' ) ) {
                parent.classList.add( 'is-focus' );
            }

            if ( parent.classList.contains( 'gfield_error' ) ) {
                parent.classList.remove( 'gfield_error' );

                // Hide validation message on default inputs.
                parent.querySelector( '.validation_message' ).style.display = 'none';
            }

            // Remove default valid consent field.
            if ( parent.classList.contains( 'material--consent' ) && ! input.checked ) {
                parent.classList.remove( 'is-valid' );
            }
        } else {
            parent.classList.remove( 'is-valid' );
            parent.classList.add( 'is-invalid' );
        }
    }

    /**
     * Add action on `focus`.
     *
     * @param event
     */
    onFocus( event ) {
        let parent = event.target.parentNode.parentNode;
        if ( parent.classList.contains( 'ginput_complex' ) || parent.classList.contains( 'clear-multi' ) ) {
            parent = event.target.parentNode;
        }

        parent.classList.add( 'is-focus' );
    }

    // Add action onBlur
    /**
     * Add action on `blur`.
     *
     * @param event
     */
    onBlur( event ) {
        this.onValidate( event.target );
        this.inputEnteredByClick = false;
    }

    /**
     * set multiple max amount behaviour.
     *
     * @param element specific element.
     * @param maxSelected Maximum amount to be selected.
     */
    selectMultipleMax( element, maxSelected ) {
        const counter_id = element.getAttribute( 'id' ).replace( 'input', 'counter' );

        element.addEventListener( 'change', ( e ) => {

            // Get amount of selected options
            let amountSelected = Array.from( element.options ).filter( function ( option ) {
                return option.selected;
            } ).map( function ( option ) {
                return option.value;
            } );

            // remove selected if there're tomuch selected options.
            if ( amountSelected.length > maxSelected ) {

                const values = jQuery( element ).val().toString().split( ',' );

                // Unset maxSelected.
                jQuery( 'option[value="' + values[ maxSelected ] + '"]' ).prop( 'selected', false );

                // Unset a option after maxSelected.
                for ( let i = maxSelected, j = amountSelected.length; i < j; i++ ) {
                    jQuery( 'option[value="' + values[ i ] + '"]' ).prop( 'selected', false );
                }
            }

            // Update counter.
            const counter = document.getElementById( counter_id );
            if ( counter ) {
                counter.innerHTML = (
                    amountSelected.length <= maxSelected
                        ? amountSelected.length
                        : maxSelected
                );
            }
        } );
    }


    /**
     * Add new element with specific parameters.
     *
     * @param type The type of element.
     * @param label The label of the element.
     * @param referenceNode The element which we need to add the element behind.
     * @param attributes Object of attributes to parse with the new element.
     * @param html Check if is HTML or not.
     * @param before Before or after referenceNode.
     */
    addElement( type, label, referenceNode, attributes = {}, html = false, before = false ) {

        if ( ! label || ! type ) {
            return;
        }

        // create a new div element
        const el = document.createElement( type );
        let elLabel;
        if ( ! html ) {
            // and give it some content
            elLabel = document.createTextNode( label );
        }

        if ( attributes ) {
            Object.keys( attributes ).forEach( attr => {
                el.setAttribute( attr, attributes[ attr ] );
            } );
        }

        if ( html ) {
            el.innerHTML = label;
        }

        if ( ! html && elLabel ) {
            // Add the text node to the newly created div
            el.appendChild( elLabel );
        }

        if ( before ) {
            referenceNode.parentNode.insertBefore( el, referenceNode );
        } else {

            // Add the newly created element and its content into the DOM.
            referenceNode.parentNode.insertBefore( el, referenceNode.nextSibling );
        }
    }

    /**
     * Loop through all fieldsets with tooltips.
     */
    fieldsetsWithTooltips() {
        const fieldsets = this.form.getElementsByClassName( 'has-tooltip' );
        if ( 0 === fieldsets.length ) {
            return;
        }
        Array.from( fieldsets )?.forEach( fieldset => this.calculateTooltipTopOffset( fieldset ) );
    }

    /**
     * Set CSS variable for the tooltip height.
     *
     * @param {HTMLElement} fieldset The HTML of the fieldset.
     */
    calculateTooltipTopOffset( fieldset ) {
        if ( ! fieldset ) {
            // Nothing to calculate.
            return;
        }

        let offset = 0;

        if ( !! fieldset.classList.contains( 'material--complex' ) ) {

            const fields = fieldset.querySelectorAll( 'input,select,textarea' );
            if ( 0 === fields.length ) {
                // Nothing to calculate.
                return;
            }

            offset = fields[ 0 ].offsetTop;

            if ( fieldset.classList.contains( 'field_sublabel_above' ) ) {
                // Append with label height and half of the gap.
                offset += ( fields[ 0 ].previousElementSibling.clientHeight / 2 ) + 4;
            }

            if ( fieldset.classList.contains( 'has-description' ) && fieldset.classList.contains( 'field_description_above' ) ) {
                offset += fieldset.querySelector( '.gfield_description' ).clientHeight + 8;

                if ( ! fieldset.classList.contains( 'field_sublabel_above' ) ) {
                    // Add another gap.
                    offset += 8;
                }
            }

            if ( fieldset.classList.contains( 'has-description' )
                && fieldset.classList.contains( 'field_description_below' )
                && fieldset.classList.contains( 'field_sublabel_below' )
            ) {
                offset += 14;
            }
        }

        if ( !! fieldset.classList.contains( 'material--simple' ) ) {
            if ( fieldset.classList.contains( 'has-description' ) && fieldset.classList.contains( 'field_description_below' ) ) {
                offset += 16;
            }

            if ( fieldset.classList.contains( 'has-description' ) && fieldset.classList.contains( 'field_description_above' ) ) {
                offset += fieldset.querySelector( 'input,select,textarea' ).offsetTop;
                offset += 14;
            }
        }

        if ( offset > 0 ) {
            fieldset.style.setProperty( '--tooltop-top', `${ offset }px` );
        }
    }

    disableMaterialWhenDescriptionAndTop() {
        Array.from( this.form.querySelectorAll( '.field_description_above.has-description:not(.material--consent)' ))?.forEach( fieldset => fieldset.classList.add( 'material--disabled' ) );
    }
}
